import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnChanges,
    OnDestroy,
    OnInit,
} from '@angular/core';
import {
    FormBuilder,
    FormGroup,
} from '@angular/forms';
import { AppStateFacade } from '@app-state';
import { UiSettingForm } from '@components/ui-forms/ui-setting-form';
import UiValidators, { UI_MAX_LOCKOUT_SECONDS } from '@validators';

import { takeUntil } from 'rxjs/operators';

const DISABLED_CONTROLS = ['hasSpecialCharacter', 'hasLowerCase', 'hasUpperCase', 'hasDigits',
    'isEnabled', 'shouldChangePasswordAfterFirstLogin'];

const PASSWORD_COMPLEXITY = ['length', 'hasSpecialCharacter', 'hasDigits', 'hasLowerCase', 'hasUpperCase'];
const PASSWORD = ['defaultExpirationDays', 'previousUseLimit', 'shouldChangePasswordAfterFirstLogin'];
const LOCKOUT = ['isEnabled', 'defaultAccountLockoutSeconds', 'maxAccessAttempts'];
const ROBOT = ['runDisconnected'];

@Component({
    selector: 'ui-security-settings',
    templateUrl: './ui-security-settings.component.html',
    styleUrls: ['../ui-setting-form.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UiSecuritySettingsComponent extends UiSettingForm implements OnInit, OnChanges, OnDestroy {
    public formGroup!: FormGroup;

    constructor(
        private _fb: FormBuilder,
        cd: ChangeDetectorRef,
        appState: AppStateFacade,
    ) {
        super(
            cd,
            appState,
        );
    }

    ngOnInit() {
        const group: any = {};
        PASSWORD_COMPLEXITY.forEach(key => group[key] = [(this.settings.security.passwordComplexity as any)[key]]);
        PASSWORD.forEach(key => group[key] = [(this.settings.security.password as any)[key]]);
        LOCKOUT.forEach(key => group[key] = [(this.settings.security.lockout as any)[key]]);
        ROBOT.forEach(key => group[key] = [(this.settings.security.robot as any)[key]]);

        this.formGroup = this._fb.group(group);

        this.isReadOnly$
            .pipe(takeUntil(this._destroyed$))
            .subscribe(isReadOnly => {
                if (isReadOnly) {
                    DISABLED_CONTROLS.forEach(controlName => this.formGroup.get(controlName)!.disable());
                } else {
                    DISABLED_CONTROLS.forEach(controlName => this.formGroup.get(controlName)!.enable());
                }
            });

        this.formGroup.valueChanges
            .pipe(takeUntil(this._destroyed$))
            .subscribe(values => {
                PASSWORD_COMPLEXITY.forEach(key => (this.settings.security.passwordComplexity as any)[key] = values[key]);
                PASSWORD.forEach(key => (this.settings.security.password as any)[key] = values[key]);
                LOCKOUT.forEach(key => (this.settings.security.lockout as any)[key] = values[key]);
                ROBOT.forEach(key => (this.settings.security.robot as any)[key] = values[key]);
            });

        this._initFormValidators();
    }

    private _initFormValidators() {
        this.formGroup.get('length')!.setValidators([
            UiValidators.range(6, 14),
        ]);
        this.formGroup.get('defaultExpirationDays')!.setValidators([
            UiValidators.range(0, 120),
            UiValidators.required(),
        ]);
        this.formGroup.get('previousUseLimit')!.setValidators([
            UiValidators.range(0, 10),
            UiValidators.required(),
        ]);
        this.formGroup.get('defaultAccountLockoutSeconds')!.setValidators([
            UiValidators.range(1, UI_MAX_LOCKOUT_SECONDS),
        ]);
        this.formGroup.get('maxAccessAttempts')!.setValidators([
            UiValidators.range(2, 10),
        ]);
        this.formGroup.get('runDisconnected')!.setValidators([
            UiValidators.range(0, 168),
            UiValidators.required(),
        ]);
    }
}
